import { authService } from "@/services/auth/auth-service/auth-service";
import { authExchange } from "@urql/exchange-auth";
import { requestPolicyExchange } from "@urql/exchange-request-policy";
import { createClient as createWsClient } from "graphql-ws";
import {
  Client,
  cacheExchange,
  fetchExchange,
  subscriptionExchange,
} from "urql";

import { errorReportingExchange } from "./error-reporting-exchange";

const CACHE_TTL_MS = 30 * 60 * 1000;

export function createApiClient(httpUrl: string, wsUrl?: string): Client {
  const exchanges = [
    requestPolicyExchange({ ttl: CACHE_TTL_MS }),
    cacheExchange,
    authExchange(async (utils) => {
      let tokens = await authService.getStoredTokens();
      if (!tokens || authService.isTokenExpired(tokens)) {
        await authService.ensureAuthenticated();
        tokens = await authService.getStoredTokens();
      }

      return {
        addAuthToOperation(operation) {
          if (!tokens?.accessToken) {
            return operation;
          }
          return utils.appendHeaders(operation, {
            Authorization: `Bearer ${tokens.accessToken}`,
          });
        },
        willAuthError() {
          return !tokens || authService.isTokenExpired(tokens);
        },
        didAuthError(error) {
          return error.graphQLErrors.some(
            (e) => e.extensions?.code === "AUTH_NOT_AUTHENTICATED"
          );
        },
        async refreshAuth() {
          await authService.refreshTokens();
          tokens = await authService.getStoredTokens();
        },
      };
    }),
    errorReportingExchange,
    fetchExchange,
  ];

  if (wsUrl) {
    const wsClient = createWsClient({
      url: wsUrl,
      lazy: true,
      retryAttempts: 3,
      retryWait: async (attempt) => {
        await new Promise((r) =>
          setTimeout(r, Math.min(1000 * 2 ** attempt, 4000))
        );
      },
      connectionParams: async () => {
        const t = await authService.getStoredTokens();
        return t?.accessToken
          ? { Authorization: `Bearer ${t.accessToken}` }
          : {};
      },
    });

    exchanges.push(
      subscriptionExchange({
        forwardSubscription(request) {
          return {
            subscribe(sink) {
              const unsubscribe = wsClient.subscribe(
                { ...request, query: request.query ?? "" },
                sink
              );
              return { unsubscribe };
            },
          };
        },
      })
    );
  }

  return new Client({ url: httpUrl, exchanges });
}
